import { useEffect, useState } from 'react'
import {
  Badge,
  Button,
  Empty,
  List,
  Popover,
  Spin,
  Typography,
} from 'antd'
import { BellOutlined, CheckOutlined, MenuOutlined, LogoutOutlined, ArrowRightOutlined, ShoppingCartOutlined } from '@ant-design/icons'
import { useNavigate } from 'react-router-dom'
import { useCartStore } from '../../stores/cart'
import { useTranslation } from 'react-i18next'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import apiClient from '../../api/client'
import LanguageSwitcher from '../../components/LanguageSwitcher'

interface PortalHeaderProps {
  pageTitle?: string
  onMenuToggle: () => void
}

interface PortalMe {
  id: number
  email: string
  full_name?: string
  customer_id: number
  customer?: {
    company?: string
    first_name?: string
    last_name?: string
    email?: string
  }
}

interface Notif {
  id: number
  type: string
  message: string
  link?: string | null
  read_at?: string | null
  created_at: string
}

export default function PortalHeader({ pageTitle, onMenuToggle }: PortalHeaderProps) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [me, setMe] = useState<PortalMe | null>(null)

  useEffect(() => {
    apiClient
      .get<{ data: PortalMe }>('/api/v1/portal/me')
      .then((res) => setMe(res.data.data))
      .catch(() => navigate('/portal/login'))
  }, [navigate])

  const logout = async () => {
    await apiClient.post('/api/v1/portal/auth/logout').catch(() => undefined)
    navigate('/portal/login')
  }

  const display = me?.customer?.company
    || [me?.customer?.first_name, me?.customer?.last_name].filter(Boolean).join(' ')
    || me?.full_name
    || me?.email
    || ''

  return (
    <header className="header">
      <div className="header-left">
        <Button
          className="header-menu-toggle"
          type="text"
          icon={<MenuOutlined />}
          onClick={onMenuToggle}
          aria-label={t('portalHeader.menuOpen', 'Menü öffnen')}
        />
        {pageTitle && <Typography.Text className="header-title">{pageTitle}</Typography.Text>}
      </div>
      <div className="header-actions">
        <LanguageSwitcher />
        <CartHeaderButton />
        <PortalNotificationBell />
        <span className="header-avatar cursor-default" title={display}>
          {(display || 'K').charAt(0).toUpperCase()}
        </span>
        <span className="header-user">{display}</span>
        <Button type="text" icon={<LogoutOutlined />} onClick={logout}
          title={t('auth.logout', 'Abmelden')} aria-label={t('auth.logout', 'Abmelden')} />
      </div>
    </header>
  )
}

// ── CartHeaderButton ────────────────────────────────────────────────────────

function CartHeaderButton() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const count = useCartStore((s) => s.items.length)
  return (
    <Badge count={count} size="small" offset={[-2, 2]}>
      <Button
        type="text"
        icon={<ShoppingCartOutlined />}
        onClick={() => navigate('/portal/cart')}
        aria-label={t('portalHeader.cart', 'Warenkorb')}
      />
    </Badge>
  )
}

// ── PortalNotificationBell ────────────────────────────────────────────

function PortalNotificationBell() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const qc = useQueryClient()
  const [open, setOpen] = useState(false)

  const countQ = useQuery({
    queryKey: ['portal-notif-count'],
    queryFn: async () => {
      const r = await apiClient.get<{ data: { unread: number } }>(
        '/api/v1/portal/notifications/count')
      return r.data.data.unread
    },
    refetchInterval: 60_000,
    staleTime: 30_000,
  })

  const listQ = useQuery({
    queryKey: ['portal-notif-list'],
    queryFn: async () => {
      const r = await apiClient.get<{ data: { items: Notif[]; unread: number } }>(
        '/api/v1/portal/notifications')
      return r.data.data.items
    },
    enabled: open,
    staleTime: 10_000,
  })

  const markAll = useMutation({
    mutationFn: () => apiClient.post('/api/v1/portal/notifications/read-all'),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['portal-notif-count'] })
      qc.invalidateQueries({ queryKey: ['portal-notif-list'] })
    },
  })

  const markOne = useMutation({
    mutationFn: (id: number) => apiClient.post(`/api/v1/portal/notifications/${id}/read`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['portal-notif-count'] })
      qc.invalidateQueries({ queryKey: ['portal-notif-list'] })
    },
  })

  const handleClick = (n: Notif) => {
    if (!n.read_at) markOne.mutate(n.id)
    if (n.link) {
      setOpen(false)
      navigate(n.link)
    }
  }

  const fmtTime = (iso: string) => {
    const diffMin = Math.floor((Date.now() - new Date(iso).getTime()) / 60000)
    if (diffMin < 1) return t('notif.justNow', 'gerade eben')
    if (diffMin < 60) return t('notif.minsAgo', '{{m}} Min.', { m: diffMin })
    const h = Math.floor(diffMin / 60)
    if (h < 24) return t('notif.hoursAgo', '{{h}} Std.', { h })
    return new Date(iso).toLocaleDateString('de-DE')
  }

  const unread = countQ.data ?? 0
  const items = listQ.data ?? []

  const content = (
    <div className="w-320">
      <div className="notif-dropdown-header">
        <Typography.Text strong>{t('notif.title', 'Benachrichtigungen')}</Typography.Text>
        {unread > 0 && (
          <Button size="small" type="link" icon={<CheckOutlined />}
            loading={markAll.isPending}
            onClick={() => markAll.mutate()}>
            {t('notif.markAllRead', 'Alle gelesen')}
          </Button>
        )}
      </div>
      {listQ.isLoading ? (
        <div className="p-24 text-center"><Spin size="small" /></div>
      ) : items.length === 0 ? (
        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={t('notif.empty', 'Keine Benachrichtigungen')}
          className="py-24" />
      ) : (
        <>
          <List
            size="small"
            dataSource={items.slice(0, 10)}
            style={{ maxHeight: 340, overflowY: 'auto' }}
            renderItem={(n) => (
              <List.Item
                key={n.id}
                style={{
                  cursor: n.link ? 'pointer' : 'default',
                  background: n.read_at ? undefined : 'var(--notif-unread-bg, rgba(22,119,255,0.06))',
                  padding: '8px 12px',
                }}
                onClick={() => handleClick(n)}
              >
                <div className="w-full">
                  <div className="flex-between gap-8">
                    <Typography.Text className="text-base flex-1">{n.message}</Typography.Text>
                    {!n.read_at && (
                      <span style={{ width: 8, height: 8, borderRadius: '50%',
                        background: 'var(--branding-accent, #0EA5E9)',
                        flexShrink: 0, marginTop: 4 }} />
                    )}
                  </div>
                  <Typography.Text type="secondary" className="text-xs">
                    {fmtTime(n.created_at)}
                  </Typography.Text>
                </div>
              </List.Item>
            )}
          />
          <div
            style={{
              borderTop: '1px solid var(--border-color-split, #f0f0f0)',
              padding: '8px 12px',
              textAlign: 'center',
            }}
          >
            <Button
              type="link"
              size="small"
              icon={<ArrowRightOutlined />}
              iconPosition="end"
              onClick={() => { setOpen(false); navigate('/portal/notifications') }}
            >
              {t('notif.viewAll', 'Alle Benachrichtigungen')}
            </Button>
          </div>
        </>
      )}
    </div>
  )

  return (
    <Popover
      content={content}
      trigger="click"
      open={open}
      onOpenChange={setOpen}
      placement="bottomRight"
      arrow={false}
      overlayInnerStyle={{ padding: 0, borderRadius: 8, overflow: 'hidden' }}
    >
      <Badge count={unread} overflowCount={99} size="small" offset={[-2, 2]}>
        <Button
          type="text"
          icon={<BellOutlined />}
          aria-label={t('notif.bell', 'Benachrichtigungen')}
        />
      </Badge>
    </Popover>
  )
}
